import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Footprints, CalendarDays, Award } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { DIFFICULTY_STYLES, money, thumb, srcSet, routeHref } from "@/lib/helpers";

const MONTHS_ES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
const MONTHS_EN = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export const RouteCard = ({ route, index = 0, availability = false }) => {
  const { t, lang } = useApp();
  const todayISO = new Date().toISOString().slice(0, 10);
  const upcoming = (route.departures || [])
    .filter((d) => d.date >= todayISO)
    .sort((a, b) => a.date.localeCompare(b.date));
  const nextDep = upcoming[0] || null;
  const nextDate = nextDep?.date || null;
  const spotsLeft = upcoming.reduce((sum, d) => sum + Math.max((d.capacity || 0) - (d.booked || 0), 0), 0);
  const spotState = upcoming.length === 0 ? null : spotsLeft <= 0 ? "sold" : spotsLeft <= 5 ? "last" : "ok";
  const title = lang === "en" && route.name_en ? route.name_en : route.name;

  const days = nextDep?.days || 1;
  const startD = nextDate ? new Date(nextDate + "T12:00:00") : null;
  const endD = startD ? new Date(startD.getTime() + (days - 1) * 86400000) : null;
  const months = lang === "en" ? MONTHS_EN : MONTHS_ES;
  const dayLabel = startD ? (days > 1 ? `${startD.getDate()}|${endD.getDate()}` : `${startD.getDate()}`) : "";
  const monthLabel = startD ? months[startD.getMonth()] : "";
  const durationLabel = days > 1
    ? `${days} ${lang === "en" ? "days" : "días"}`
    : (lang === "en" ? "1 day" : "1 día");

  return (
    <Link
      to={routeHref(route)}
      data-testid={`route-card-${route.route_id}`}
      className="group block rounded-xl bg-white border border-[#E2DFD7] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:shadow-[0_16px_44px_rgb(0,0,0,0.12)] hover:-translate-y-1 transition-transform duration-300 animate-rise"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="relative h-52 overflow-hidden">
        <img src={thumb(route.hero_image, 640)} alt={title} loading="lazy" decoding="async"
          srcSet={srcSet(route.hero_image, [320, 480, 640])}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          style={{ objectPosition: route.hero_position || "center" }}
          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className={`absolute top-3 left-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${DIFFICULTY_STYLES[route.difficulty]}`}>
          <Award className="h-3.5 w-3.5" /> {t.difficulty[route.difficulty]}
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-xl font-semibold tracking-tight text-[#16221D] leading-snug line-clamp-3">{title}</h3>
          {startD && (
            <div className="shrink-0 pl-3 text-center leading-none" data-testid={`card-next-date-${route.route_id}`}>
              <p className="text-xl font-bold text-[#16221D] tabular-nums">{dayLabel}</p>
              <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-[#5E6D64]">{monthLabel}</p>
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-[#5E6D64]">
          <span className="inline-flex items-center gap-1 border border-[#E2DFD7] rounded-md px-2 py-1"><MapPin className="h-3.5 w-3.5" />{route.state}</span>
          {nextDep && <span className="inline-flex items-center gap-1 border border-[#E2DFD7] rounded-md px-2 py-1"><CalendarDays className="h-3.5 w-3.5" />{durationLabel}</span>}
          <span className="inline-flex items-center gap-1 border border-[#E2DFD7] rounded-md px-2 py-1"><Footprints className="h-3.5 w-3.5" />{route.distance_km} km</span>
        </div>
        {availability && spotState && (
          <p className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
            spotState === "sold" ? "bg-[#FDECEC] text-[#B91C1C]"
              : spotState === "last" ? "bg-[#FEF3C7] text-[#B45309]" : "bg-[#E6F4EA] text-[#15803D]"
          }`} data-testid={`card-availability-${route.route_id}`}>
            {spotState === "sold" ? (lang === "es" ? "Lugares agotados" : "Sold out")
              : spotState === "last" ? t.card.lastSpots : t.card.available}
          </p>
        )}
        <div className="mt-5 flex items-end justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-[#8A968E]">{t.card.from}</p>
            <p className="text-2xl font-bold text-[#16221D]">{money(route.price_from || route.price)} <span className="text-[10px] align-super text-[#8A968E]">MXN</span></p>
          </div>
          <span className="rounded-full bg-[#6E7E56] text-white text-sm px-5 py-2 group-hover:bg-[#D94E28] transition-colors duration-300">
            {t.card.details}
          </span>
        </div>
      </div>
    </Link>
  );
};
